import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import * as moment from 'moment';
import { getParams } from '../common/common.func';
import { PagedList } from '../common/common.types';
import { AzcMoment } from '../common/datetime/azc-moment.const';
import { EventPlannerFilter, PlannedEvent } from './event-planner.types';
import { Event, EventFilter, EventStatus, EventStatusChange, EventsToActionFilter, EventSummary, EventToAction, EventUpdateRequest, NewEvent, UpdatedEvent } from './event.types';
import { QuoteSummary } from '../pricing/price-matrix.types';

@Injectable()
export class EventService {
	private eventsUrl = 'api/events';
    private coreApiUrl = 'core-api';
	private eventsToActionUrl = this.coreApiUrl + '/api/v1/events/actionableEvents';
	private eventsToPlanUrl = 'api/plannerEvents';
	private eventStatusUrl = (eventId: number, status: EventStatus) => `api/events/${eventId}/status/${status}`;

	constructor(private _http: HttpClient) { }

	getEvents(filter?: EventFilter): Promise<PagedList<EventSummary>> {
		const actualFilter: EventFilter = {
			...filter
		};
		actualFilter.eventBefore = AzcMoment.formatEventDate(actualFilter.eventBefore);
		actualFilter.eventAfter = AzcMoment.formatEventDate(actualFilter.eventAfter);
		const options = { params: getParams(actualFilter) };
		return this._http.get<PagedList<EventSummary>>(this.eventsUrl, options).toPromise();
	}

	getEventsToAction(filter: EventsToActionFilter): Promise<PagedList<EventToAction>> {
		const options = { params: getParams(filter) };
		return this._http.get<PagedList<EventToAction>>(this.eventsToActionUrl, options).toPromise();
	}

	getEventsToPlan(filter: EventPlannerFilter): Promise<PagedList<PlannedEvent>> {
		const actualFilter: EventPlannerFilter = {
			...filter
		};
		actualFilter.eventBefore = AzcMoment.formatEventDate(actualFilter.eventBefore);
		actualFilter.eventAfter = AzcMoment.formatEventDate(actualFilter.eventAfter);

		const options = { params: getParams(actualFilter) };

		return this._http.get<PagedList<PlannedEvent>>(this.eventsToPlanUrl, options).toPromise();
	}

	getEvent(eventId: number): Promise<Event> {
		return this._http.get<Event>(`${this.eventsUrl}/${eventId}`).toPromise();
	}

	createEvent(newEvent: NewEvent): Promise<number> {
		const eventData = {...newEvent};

		if (newEvent.hasOwnProperty('startsOn')) { eventData.startsOn = AzcMoment.formatEventDate(newEvent.startsOn)}
		if (newEvent.hasOwnProperty('endsOn')) { eventData.endsOn = AzcMoment.formatEventDate(newEvent.endsOn)}

		return this._http.post<number>(this.eventsUrl, eventData).toPromise();
	}

	updateEvent(eventId: number, updatedEvent: UpdatedEvent): Promise<void> {
		const eventData = {...updatedEvent};

		if (updatedEvent.hasOwnProperty('startsOn')) { eventData.startsOn = AzcMoment.formatEventDate(updatedEvent.startsOn)}
		if (updatedEvent.hasOwnProperty('endsOn')) { eventData.endsOn = AzcMoment.formatEventDate(updatedEvent.endsOn)}
		if (updatedEvent.hasOwnProperty('depositDue')) { eventData.depositDue = AzcMoment.formatEventDate(updatedEvent.depositDue)}
		if (updatedEvent.hasOwnProperty('paymentDue')) { eventData.paymentDue = AzcMoment.formatEventDate(updatedEvent.paymentDue)}

		return this._http.put<void>(`${this.eventsUrl}/${eventId}`, eventData).toPromise();
	}

	updateEventStatus(eventId: number, status: EventStatus, statusChange: EventStatusChange): Promise<number> {
		if (statusChange.hasOwnProperty('actionOn')) { statusChange.actionOn = AzcMoment.formatEventDate(statusChange.actionOn) }
		if (statusChange.hasOwnProperty('rescheduleRequestDate')) { statusChange.rescheduleRequestDate = AzcMoment.formatEventDate(statusChange.rescheduleRequestDate) }
		if (statusChange.hasOwnProperty('rescheduleRequestStatus')) { statusChange.rescheduleRequestStatus = statusChange.rescheduleRequestStatus }
		if (statusChange.hasOwnProperty('depositDue')) { statusChange.depositDue = AzcMoment.formatEventDate(statusChange.depositDue) }
		if (statusChange.hasOwnProperty('paymentDue')) { statusChange.paymentDue = AzcMoment.formatEventDate(statusChange.paymentDue) }

		return this._http.put<number>(this.eventStatusUrl(eventId, status), statusChange).toPromise();
	}

	requestEventUpdate(eventId: number, eventUpdateRequest: EventUpdateRequest): Promise<void> {
		return this._http.post<void>(`${this.eventsUrl}/${eventId}/requests`, eventUpdateRequest).toPromise();
	}

	previewBookingConfirmation(eventId: number, conciergeNotes?: string): Promise<string> {
		const options = {
			params: { conciergeNotes },
			responseType: 'text' as 'text'
		};

		return this._http.get(`${this.eventsUrl}/${eventId}/emails/bookingConfirmation`, options).toPromise();
	}

	sendBookingConfirmation(eventId: number, conciergeNotes?: string): Promise<void> {
		const options = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		return this._http.post<void>(`${this.eventsUrl}/${eventId}/emails/bookingConfirmation`, JSON.stringify(conciergeNotes), options).toPromise();
	}

	previewFinalConfirmation(eventId: number, conciergeNotes?: string): Promise<string> {
		const options = {
			params: { conciergeNotes },
			responseType: 'text' as 'text'
		};

		return this._http.get(`${this.eventsUrl}/${eventId}/emails/finalConfirmation`, options).toPromise();
	}

	sendFinalConfirmation(eventId: number, conciergeNotes?: string): Promise<void> {
		const options = {
			headers: {
				'Content-Type': 'application/json'
			}
		};

		return this._http.post<void>(`${this.eventsUrl}/${eventId}/emails/finalConfirmation`, JSON.stringify(conciergeNotes), options).toPromise();
	}

	deleteEvent(eventId: number): Promise<void> {
		return this._http.delete<void>(`${this.eventsUrl}/${eventId}`).toPromise();
	}
	sendQuoteSummary(eventId: number, quoteSummary: QuoteSummary): Promise<void> {
		return this._http.post<void>(`${this.eventsUrl}/${eventId}/emails/quotesummary`, quoteSummary).toPromise();
	}
	customerRequestToBookFromQuoteEmail(eventId: number, token: string, email: string): Promise<void> {
		const url = `${this.eventsUrl}/${eventId}/requesttobook?email=${encodeURIComponent(email)}&token=${encodeURIComponent(token)}`;
		return this._http.get<void>(url).toPromise<void>();
	}
    
    async rescheduleEventApproval(eventId: number, isApproved: boolean): Promise<void> {
        let action = isApproved ? 'approve' : 'reject';
		this._http.post('core-api/api/v1/events/' + eventId + '/reschedule/' + action, { "requestedByNote" : "hquser"}).toPromise();
    }
    
    async rescheduleEventRequest(eventId: number, reschedulRequestDate? : Date): Promise<void> {
        await this._http.post('core-api/api/v1/events/' + eventId + '/reschedule',
            {
                "newDate": reschedulRequestDate,
                "requestedByNote": "hquser",
                "adminOverride": true,
            }
        ).toPromise();
    }

    async updateScheduledDate(eventId: number, newDate: string | moment.Moment): Promise<void> {
        await this._http.patch(
            'core-api/api/v1/events/' + eventId + '/schedule-date',
            { newDate: AzcMoment.formatEventDate(newDate) }
        ).toPromise();
    }

	async updateEventUserFormResponse(eventId: number, body: {
		userResponse: any;
	}) {
		await this._http.put('core-api/api/v1/events/' + eventId + '/user-form-response', 
            body,
        ).toPromise();
	}

	async fetchEventUserResponse(eventId: number) {
		return this._http.get('core-api/api/v1/events/' + eventId + '/user-form-response'
        ).toPromise();
	}
        
}
